import React from 'react'
import { useIntl } from 'react-intl'

import { Check, Lock } from 'lucide-react'

import { Button } from '@/components/atoms/Button/Button'
import { type PromotionOption } from '@/components/model/PromotionOption'
import { PromotionOptionPreview } from '@/components/molecules/PromotionOptionPreview'
import { formatPromotionFee } from '@/components/utils/formatPromotionFee'
import { cn } from '@/lib/utils'

export interface PromotionOptionCardProps extends React.HTMLAttributes<HTMLElement> {
  readonly option: PromotionOption
  readonly onAdd?: () => void
  readonly onRemove?: () => void
}

const details = ['position', 'moment', 'format', 'slots', 'content'] as const

export const PromotionOptionCard = React.forwardRef<HTMLElement, PromotionOptionCardProps>(
  ({ option, onAdd, onRemove, className, ...props }, ref) => {
    const intl = useIntl()
    const { placement, state, fee, suggestedPrice } = option
    const title = intl.formatMessage({ id: `promotions.shop.options.${placement}.title` })
    return (
      <article
        ref={ref}
        aria-label={title}
        data-state={state}
        className={cn('flex flex-col overflow-hidden rounded-[4px] border border-gray-300 bg-white', className)}
        {...props}>
        <PromotionOptionPreview
          placement={placement}
          label={intl.formatMessage({ id: 'promotions.shop.card.preview' }, { title })}
        />
        <div className="flex grow flex-col gap-3 p-4">
          <div className="flex items-baseline justify-between gap-3">
            <div className="flex min-w-0 flex-col gap-1">
              <h2 className="font-heading text-lg font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-500">
                {intl.formatMessage({ id: `promotions.shop.options.${placement}.audience` })}
              </p>
            </div>
            <p className="text-lg whitespace-nowrap text-gray-900">{formatPromotionFee(intl, fee)}</p>
          </div>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm leading-[1.35]">
            {details.map((detail) => (
              <React.Fragment key={detail}>
                <dt className="text-gray-500">{intl.formatMessage({ id: `promotions.shop.card.${detail}` })}</dt>
                <dd className="text-gray-900">
                  {intl.formatMessage({ id: `promotions.shop.options.${placement}.${detail}` })}
                </dd>
              </React.Fragment>
            ))}
            <dt className="text-gray-500">{intl.formatMessage({ id: 'promotions.shop.card.suggestedPrice' })}</dt>
            <dd className="text-gray-900">
              {suggestedPrice ?? intl.formatMessage({ id: 'promotions.shop.card.noFee' })}
            </dd>
          </dl>
        </div>
        <PromotionOptionCardFooter state={state} onAdd={onAdd} onRemove={onRemove} />
      </article>
    )
  }
)

const PromotionOptionCardFooter = ({
  state,
  onAdd,
  onRemove,
}: Pick<PromotionOptionCardProps, 'onAdd' | 'onRemove'> & { state: PromotionOption['state'] }) => {
  const intl = useIntl()
  switch (state) {
    case 'available':
      return (
        <div
          data-testid="promotion-option-footer"
          className="flex h-16 shrink-0 items-center justify-end gap-3 border-t border-gray-200 bg-white px-4">
          <Button size="sm" variant="primary" onClick={onAdd}>
            {intl.formatMessage({ id: 'promotions.shop.card.add' })}
          </Button>
        </div>
      )
    case 'inPlan':
      return (
        <div
          data-testid="promotion-option-footer"
          className="flex h-16 shrink-0 items-center justify-between gap-3 border-t border-green-700 bg-green-50 px-4">
          <span className="flex items-center gap-1.5 text-base font-bold text-green-900">
            <Check className="size-4" strokeWidth={3} aria-hidden="true" />
            {intl.formatMessage({ id: 'promotions.shop.card.inPlan' })}
          </span>
          <Button size="sm" onClick={onRemove}>
            {intl.formatMessage({ id: 'promotions.shop.card.remove' })}
          </Button>
        </div>
      )
    case 'locked':
      return (
        <div
          data-testid="promotion-option-footer"
          className="flex h-16 shrink-0 items-center justify-between gap-3 border-t border-gray-400 bg-gray-100 px-4">
          <span className="flex items-center gap-1.5 text-base font-bold text-gray-900">
            <Lock className="size-4" strokeWidth={2.4} aria-hidden="true" />
            {intl.formatMessage({ id: 'promotions.shop.card.inPlan' })}
          </span>
          <span className="text-right text-sm text-gray-600">
            {intl.formatMessage({ id: 'promotions.shop.locked' })}
          </span>
        </div>
      )
  }
}
